import { z } from 'zod'

// TAG-1045 — output DTO over a STORED row: legacy rows omit these fields, so parsing must not
// throw. Defaults mirror the runtime engine (src/app/services/paymentFlowV2.ts: `weight ?? 100`,
// absent `disabled` = enabled).
// `routing` is passthrough on purpose: a missing key here would strip pins on getById and
// the next CRM save would write the jsonb without them (same wipe as disabledCardBrands).
const RoutingPinGroupSchema = z
    .object({
        amount: z
            .array(
                z
                    .object({
                        min: z.number().optional(),
                        max: z.number().optional(),
                        currency: z.string().optional(),
                    })
                    .passthrough(),
            )
            .optional(),
        brands: z.array(z.string()).optional(),
        bins: z.array(z.string()).optional(),
    })
    .passthrough()

export const ProcessorRoutingSchema = z
    .object({
        pins: z.array(RoutingPinGroupSchema).optional(),
    })
    .passthrough()

export const ProcessorConfigSchema = z.object({
    processorId: z.string(),
    weight: z.number().default(100),
    disabled: z.boolean().default(false),
    nonStickable: z.boolean().default(false),
    disabledCardBrands: z.array(z.string()).optional(),
    routing: ProcessorRoutingSchema.optional(),
})

export const FallbackProcessorConfigSchema = z.object({
    processorId: z.string(),
    orderIndex: z.number(),
})

export const AbuseDetectionConfigSchema = z.object({
    enabled: z.boolean().optional(),
    timeWindowMinutes: z.number().optional(),
    detectIpAddress: z.boolean().optional(),
    detectBin: z.boolean().optional(),
    detectEmail: z.boolean().optional(),
    ipAddressThreshold: z.number().optional(),
    binThreshold: z.number().optional(),
    emailThreshold: z.number().optional(),
    onlyFailedPayments: z.boolean().optional(),
    includeDraftPayments: z.boolean().optional(),
    autoBlockTtlHours: z.number().optional(),
    autoBlockPermanent: z.boolean().optional(),
})

export const CustomRetrySchema = z.object({
    id: z.string(),
    value: z.number(),
    unit: z.enum(['days', 'weeks']),
    description: z.string(),
})

export const RebillSettingSchema = z.object({
    isAdvancedEnabled: z.boolean(),
    customRetries: z.array(CustomRetrySchema),
    advancedOptions: z.object({
        expiredCardNoRetries: z.boolean(),
        retryOnPayrollDays: z.boolean(),
        avoidWeekends: z.boolean(),
        velocityLimitWait24h: z.boolean(),
        timeoutRetryMinutes: z.boolean(),
        code51RetryPayroll: z.boolean(),
        code05Wait48h: z.boolean()
    }),
    afterLastRetry: z.enum(['cancel', 'pause']).optional()
})

export const PaymentFlowSchema = z.object({
    id: z.string(),
    accountId: z.string(),
    name: z.string(),
    strategy: z.string(),
    processorConfigs: z.array(ProcessorConfigSchema),
    fallbackProcessorConfigs: z.array(FallbackProcessorConfigSchema).nullable(),
    fallbackMode: z.boolean().nullable(),
    maxFallbackRetries: z.number().nullable(),
    stickyProcessorEnabled: z.boolean().nullable(),
    threeDsEnabled: z.boolean().nullable(),
    pickProcessorStrategy: z.enum(['weighted', 'lowestCapacity', 'automatic']),
    abuseDetectionConfig: AbuseDetectionConfigSchema.nullable(),
    rebillSettings: RebillSettingSchema.nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
})

export const FlowProcessorSchema = z.object({
    id: z.string(),
    name: z.string(),
    type: z.string(),
    enabled: z.boolean().nullable(),
    archivedAt: z.date().nullable().optional(),
    monthlyProcessingCapacity: z.number().nullable().optional(),
    baseCurrency: z.string().nullable().optional(),
    tagadapayAccountId: z.string().nullable().optional(),
    tagadapayStatus: z.string().nullable().optional(),
    tagadapayMode: z.string().nullable().optional(),
    options: z.record(z.string(), z.unknown()).nullable().optional(),
})

export const ProcessorConfigWithProcessorSchema = ProcessorConfigSchema.extend({
    processor: FlowProcessorSchema.optional(),
})

export const FallbackProcessorConfigWithProcessorSchema = FallbackProcessorConfigSchema.extend({
    processor: FlowProcessorSchema.optional(),
})

export const PaymentFlowWithProcessorsSchema = PaymentFlowSchema.extend({
    processorConfigs: z.array(ProcessorConfigWithProcessorSchema),
    fallbackProcessorConfigs: z.array(FallbackProcessorConfigWithProcessorSchema).nullable(),
    allProcessors: z.array(FlowProcessorSchema),
})

export const PaymentFlowListItemSchema = PaymentFlowSchema

export const PaymentFlowDetailsSchema = PaymentFlowWithProcessorsSchema

export type FlowProcessorType = z.infer<typeof FlowProcessorSchema>
export type ProcessorConfigType = z.infer<typeof ProcessorConfigSchema>
export type FallbackProcessorConfigType = z.infer<typeof FallbackProcessorConfigSchema>
export type PaymentFlowType = z.infer<typeof PaymentFlowSchema>
export type ProcessorConfigWithProcessorType = z.infer<typeof ProcessorConfigWithProcessorSchema>
export type FallbackProcessorConfigWithProcessorType = z.infer<
    typeof FallbackProcessorConfigWithProcessorSchema
>
export type PaymentFlowWithProcessorsType = z.infer<typeof PaymentFlowWithProcessorsSchema>
export type PaymentFlowListItemType = z.infer<typeof PaymentFlowListItemSchema>
export type PaymentFlowDetailsType = z.infer<typeof PaymentFlowDetailsSchema>

/**
 * The DTO under a name. An interface is emitted by reference, so the CRM's AppRouter
 * declaration names it instead of spelling out the flow (and zod's passthrough internals
 * of `routing`) at every procedure: spelled out, they push AppRouter past what
 * TypeScript serializes (TS7056).
 */
export interface PaymentFlowDto extends PaymentFlowType {
    id: string
}

export const toPaymentFlowDto = (data: any): PaymentFlowDto => {
    return PaymentFlowSchema.parse(data)
}

export const toPaymentFlowWithProcessorsDto = (data: any): PaymentFlowWithProcessorsType => {
    return PaymentFlowWithProcessorsSchema.parse(data)
}

export const toPaymentFlowListItemDto = (data: any): PaymentFlowListItemType => {
    return PaymentFlowListItemSchema.parse(data)
}

export const toPaymentFlowDetailsDto = (data: any): PaymentFlowDetailsType => {
    return PaymentFlowDetailsSchema.parse(data)
}

